import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";

import { api } from "@/api/client";

/**
 * Where the shop's outbound email comes from.
 *
 * The API key is a credential and lives with the other credentials; everything
 * here is configuration an administrator should be able to see and correct.
 */
export default function EmailSection() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    api
      .settings.get()
      .then((s) =>
        setSettings({
          mailgun_domain: s.mailgun_domain ?? "",
          mailgun_region: s.mailgun_region ?? "us",
          mail_from_name: s.mail_from_name ?? "",
          mail_from_email: s.mail_from_email ?? "",
        })
      )
      .catch((err) => setError(err.message || "Could not load the email settings."));
  }, []);

  const announce = (message) => {
    setSuccess(message);
    setError("");
    setTimeout(() => setSuccess(""), 5000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.settings.update(settings);
      announce("Email settings saved.");
    } catch (err) {
      setError(err.message || "Could not save the email settings.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Prove the settings work, by sending with them.
   *
   * Saved first, because the point is to test what is stored rather than what
   * happens to be on screen.
   */
  const handleTest = async () => {
    setTesting(true);
    setError("");
    try {
      await api.settings.update(settings);
      const { to } = await api.settings.sendTestEmail();
      announce(`Sent a test message to ${to}. Check it arrived, and check the sender address.`);
    } catch (err) {
      setError(err.message || "The test message could not be sent.");
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return (
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="card-title">
          <Mail size={20} />
          Email
        </h2>
        <div className="muted">
          <Loader2 size={16} className="spin" /> Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card-title">
        <Mail size={20} />
        Email
      </h2>
      <p className="muted">
        Invitations and password reset codes are sent through Mailgun, from the
        shop&rsquo;s own domain. Add the API key under Integration credentials below.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSave}>
        <div className="user-grid">
          <div>
            <label className="form-label">Mailgun sending domain</label>
            <input
              className="form-input"
              placeholder="mg.goldstarprinting.us"
              value={settings.mailgun_domain}
              onChange={(e) => setSettings({ ...settings, mailgun_domain: e.target.value.trim() })}
            />
            <p className="field-hint">
              Exactly as it appears in Mailgun under Sending → Domains.
            </p>
          </div>

          <div>
            <label className="form-label">Region</label>
            <select
              className="form-select"
              value={settings.mailgun_region}
              onChange={(e) => setSettings({ ...settings, mailgun_region: e.target.value })}
            >
              <option value="us">US</option>
              <option value="eu">EU</option>
            </select>
            <p className="field-hint">
              A key from one region is rejected by the other, and the error
              looks exactly like a wrong key.
            </p>
          </div>
        </div>

        <div className="user-grid" style={{ marginTop: "1rem" }}>
          <div>
            <label className="form-label">From name</label>
            <input
              className="form-input"
              placeholder="Gold Star Printing"
              value={settings.mail_from_name}
              onChange={(e) => setSettings({ ...settings, mail_from_name: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">From address</label>
            <input
              className="form-input"
              type="email"
              placeholder="noreply@mg.goldstarprinting.us"
              value={settings.mail_from_email}
              onChange={(e) => setSettings({ ...settings, mail_from_email: e.target.value.trim() })}
            />
            <p className="field-hint">
              Must be on a domain Mailgun has verified, or Mailgun refuses it.
            </p>
          </div>
        </div>

        <div className="button-row" style={{ marginTop: "1.5rem" }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save email settings"}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleTest}
            disabled={testing || !settings.mailgun_domain || !settings.mail_from_email}
          >
            {testing ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            {testing ? "Sending..." : "Send a test to myself"}
          </button>
        </div>
      </form>
    </div>
  );
}
